/**
 * @module string
 * String utility functions
 */

/**
 * Capitalizes the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} The string with the first letter capitalized
 * @example
 * capitalizeFirstLetter('hello world') // returns 'Hello world'
 * capitalizeFirstLetter('') // returns ''
 * capitalizeFirstLetter(null) // returns ''
 * capitalizeFirstLetter(undefined) // returns ''
 */
export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') {
    return ''
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} The string with the first letter of each word capitalized
 * @example
 * capitalizeWords('hello world') // returns 'Hello World'
 * capitalizeWords('john doe') // returns 'John Doe'
 */
export const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') {
    return ''
  }

  return str
    .split(' ')
    .map((word) => capitalizeFirstLetter(word))
    .join(' ')
}

/**
 * Converts a string to title case (capitalizes first letter of each word)
 * @param {string} str - The string to convert to title case
 * @returns {string} The string in title case
 * @example
 * toTitleCase('hello world') // returns 'Hello World'
 * toTitleCase('john doe smith') // returns 'John Doe Smith'
 */
export const toTitleCase = (str) => {
  return capitalizeWords(str)
}

/**
 * Capitalizes a status string safely, handling null/undefined values
 * @param {string} status - The status string to capitalize
 * @returns {string} The capitalized status string or empty string if invalid
 * @example
 * capitalizeStatus('active') // returns 'Active'
 * capitalizeStatus('on-leave') // returns 'On-leave'
 * capitalizeStatus(null) // returns ''
 * capitalizeStatus(undefined) // returns ''
 */
export const capitalizeStatus = (status) => {
  if (!status || typeof status !== 'string') {
    return ''
  }

  return status.charAt(0).toUpperCase() + status.slice(1)
}
